/**
 * One label card's data, as /label/labels returns it.
 * @typedef {object} LabelEntry
 * @property {Record<string, any>} label
 * @property {?string} cropUrl
 * @property {?{x: number, y: number}} cropMarker
 * @property {?string} gsvImageUrl
 */

import i18next from 'i18next';
import { createPanoAttribution } from './common/pano-viewer/PanoAttribution.js';
import { createPanoViewerLogo } from './common/pano-viewer/PanoViewerLogo.js';
import { ShareWidget } from './common/share/ShareWidget.js';
import { util } from './common/utilities.js';
import './common/utilitiesSidewalk.js';
import '../css/components/landing-validation-grid.css';

/**
 * Landing-page grid of recently-found labels with inline Agree/Disagree/Unsure buttons (#1638), so visitors can
 * contribute useful validations straight from the home page.
 *
 * Borrows the Gallery card pattern: a static image (locally-saved crop preferred, GSV Static API as fallback — the
 * crop is free to serve while the API costs money per image) with the label-type icon overlaid where the label
 * actually is in that image (#2660). Votes POST to /labelmap/validate, which works for anonymous visitors and
 * creates its own mission server-side. Validated cards are swapped for a fresh label from a prefetched pool, giving
 * the "live" feel.
 *
 * Label data comes from POST /label/labels with sort: 'recent', i.e. a shuffled pool of the newest labels needing
 * validation. Nothing is fetched during page load; the grid fills itself once the visitor interacts with the page.
 */
export class LandingValidationGrid {
  // Width:height of the card's photo box (.lvg-card-img); crops and stills are cover-fitted into it.
  static CARD_IMAGE_ASPECT = 3 / 2;
  static #GRID_SIZE = 6;
  // Cards past the third are hidden by CSS below 650px — keep in sync with the nth-child(n+4) rule in
  // css/components/landing-validation-grid.css. A layout breakpoint, so there's no backend value to source it from.
  static #NARROW_VISIBLE_CARDS = 3;
  // The server splits n across the 6 label types validatable from a static image (static_imagery_only — Signal
  // needs a pan up its pole, so it's excluded server-side), so fetch sizes are multiples of 6: 2 per type up
  // front (6 rendered + the rest pooled for replacements), 1 per type on refills.
  static #INITIAL_FETCH = 12;
  static #REFILL_FETCH = 6;
  // Refill the pool in the background once it runs this low, so replacements stay instant.
  static #REFILL_THRESHOLD = 2;
  static #THANKS_MS = 1200;

  #section;
  #grid;
  #loadedLabelIds = new Set();
  #pool = [];
  #fetching = false;
  #firstLoadLogged = false;

  /**
   * @param {HTMLElement} sectionEl - The #landing-validation-container section (rendered with `hidden`).
   */
  constructor(sectionEl) {
    this.#section = sectionEl;
    this.#grid = sectionEl.querySelector('#landing-validation-grid');

    // Unhide and show skeleton cards immediately so the section holds its space — the real cards then swap in
    // without a layout shift. The section re-hides if the city turns out to have nothing to validate.
    this.#section.hidden = false;
    for (let i = 0; i < LandingValidationGrid.#GRID_SIZE; i++) {
      const skeleton = document.createElement('div');
      skeleton.className = 'lvg-card lvg-card-skeleton';
      this.#grid.appendChild(skeleton);
    }

    // Don't hit the server (label queries + imagery checks) until first page interaction, keeping simple crawlers from
    // hitting expensive queries frequently.
    util.onFirstInteractionOrIdle(() => this.#start());
  }

  /** Fetches the initial batch and swaps the skeletons for real cards (or hides the section if there are none). */
  async #start() {
    await this.#loadBatch(LandingValidationGrid.#INITIAL_FETCH);
    this.#grid.querySelectorAll('.lvg-card-skeleton').forEach((skeleton, index) => {
      const entry = this.#pool.shift();
      if (entry) skeleton.replaceWith(this.#buildCard(entry, index));
      else skeleton.remove();
    });

    const count = this.#grid.querySelectorAll('.lvg-card').length;
    if (count === 0) this.#section.hidden = true;
    if (!this.#firstLoadLogged) {
      this.#firstLoadLogged = true;
      window.logWebpageActivity(`View_module=LandingValidationGrid_labelCount=${count}`);
    }
  }

  /**
   * @param {LabelEntry} entry - One entry from /label/labels.
   * @param {string} imageSource - Which source the card is actually showing: 'crop' or 'api'.
   * @returns {{x: number, y: number}} Fractions of the card's 3:2 photo box (.lvg-card-img), into which the image is
   *   cover-fitted; the photo fills that box, so they are fractions of the photo element too.
   */
  static #markerFraction(entry, imageSource) {
    return util.misc.labelMarkerFraction(imageSource, entry.cropMarker, entry.label.canvas_x, entry.label.canvas_y, {
      canvasWidth: entry.label.canvas_width,
      canvasHeight: entry.label.canvas_height,
      boxAspect: LandingValidationGrid.CARD_IMAGE_ASPECT,
    });
  }

  /**
   * @param {?HTMLElement} marker - The marker element, or null for a label type with no icon.
   * @param {LabelEntry} entry - The card's entry.
   * @param {string} imageSource - Which source the card is actually showing: 'crop' or 'api'.
   */
  static #positionMarker(marker, entry, imageSource) {
    if (!marker) return;
    const { x, y } = LandingValidationGrid.#markerFraction(entry, imageSource);
    marker.style.left = `${100 * x}%`;
    marker.style.top = `${100 * y}%`;
  }

  /**
   * How many grid slots the CSS actually shows at the current viewport width.
   * @returns {number}
   */
  static #visibleCardCount() {
    return window.matchMedia('(width <= 650px)').matches
      ? LandingValidationGrid.#NARROW_VISIBLE_CARDS
      : LandingValidationGrid.#GRID_SIZE;
  }

  /**
   * Fetches n recent labels needing validation and appends the usable ones to the pool.
   * @param {number} n - How many labels to request (the server spreads this across label types).
   */
  async #loadBatch(n) {
    if (this.#fetching) return;
    this.#fetching = true;
    try {
      const response = await fetch('/label/labels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          n,
          loaded_labels: [...this.#loadedLabelIds],
          validation_options: ['unvalidated', 'unsure'],
          sort: 'recent',
          static_imagery_only: true,
        }),
      });
      if (!response.ok) throw new Error(`label fetch failed: ${response.status}`);
      const data = await response.json();

      const usable = (data.labelsOfType || []).filter((entry) =>
        !entry.label.from_current_user
        && entry.label.user_validation === null
        && (entry.cropUrl || entry.gsvImageUrl));
      usable.forEach((entry) => this.#loadedLabelIds.add(entry.label.label_id));

      // Crop-backed labels first: crops are served from our own disk while the GSV Static API is paid per image,
      // so API-backed entries are only used when there aren't enough crops to fill the grid.
      this.#pool.push(...usable.filter((entry) => entry.cropUrl), ...usable.filter((entry) => !entry.cropUrl));
    } catch (e) {
      console.error('Failed to load labels for the validation grid', e);
    } finally {
      this.#fetching = false;
    }
  }

  /**
   * Builds one card: the label image with the label-type icon marked where the label is in it, the localized
   * "Is this a …?" question, and the three validation buttons.
   * @param {LabelEntry} entry - One entry from /label/labels.
   * @param {number} index - The card's slot in the grid, which decides whether its image loads eagerly.
   * @returns {HTMLElement}
   */
  #buildCard(entry, index) {
    const label = entry.label;
    const typeKebab = util.camelToKebab(label.label_type);
    const card = document.createElement('figure');
    card.className = 'lvg-card';
    // Which source actually loaded, reported as viewer_type with the validation ('crop' → StaticCrop).
    card.dataset.imageSource = entry.cropUrl ? 'crop' : 'api';

    const imgWrap = document.createElement('div');
    imgWrap.className = 'lvg-card-img';
    const img = document.createElement('img');
    img.className = 'lvg-card-photo';
    // Eager only for a crop-backed card in a slot this width actually shows, so the grid isn't blank on arrival:
    // browser lazy-loading holds the fetch until the image nears the viewport, which would undo the early start.
    // Crops are served from our own disk, so warming one costs bandwidth and nothing else. An API-backed card stays
    // lazy however visible it is — gsvImageUrl is the Street View Static API, billed per request, and this now runs
    // for every engaged visitor rather than only the ones who scroll two-thirds down the page.
    const freeToWarm = entry.cropUrl && !util.saveDataEnabled();
    img.loading = freeToWarm && index < LandingValidationGrid.#visibleCardCount() ? 'eager' : 'lazy';
    img.alt = util.misc.labelTypeName(label.label_type);
    // Set once the overlays are on the card, below; the error handler fires on a later event, so it exists by then.
    let creditImage = null;
    img.addEventListener('error', () => {
      // The saved crop can 404 (signed URLs expire after a while); fall back to the GSV Static API image. A card
      // whose every source fails is dead weight — swap it for a fresh label.
      if (card.dataset.imageSource === 'crop' && entry.gsvImageUrl) {
        card.dataset.imageSource = 'api';
        // The crop's recorded position describes the crop only; the still is the Explore frame again.
        LandingValidationGrid.#positionMarker(imgWrap.querySelector('.lvg-card-marker'), entry, 'api');
        creditImage?.('api');
        img.src = entry.gsvImageUrl;
      } else {
        this.#replaceCard(card);
      }
    });
    img.src = entry.cropUrl || entry.gsvImageUrl;
    imgWrap.appendChild(img);

    const iconPath = util.misc.getIconImagePaths(label.label_type)?.iconImagePath;
    if (iconPath) {
      const marker = document.createElement('img');
      marker.className = 'lvg-card-marker';
      marker.src = iconPath;
      marker.alt = '';
      LandingValidationGrid.#positionMarker(marker, entry, card.dataset.imageSource);
      imgWrap.appendChild(marker);
    }
    // The credit owed on a crop, our cut of someone else's panorama (#4865, #5202); the still brands itself (see
    // PanoViewerLogo). Only licensed imagery carries a licence, so createPanoAttribution hides itself without one.
    const logo = createPanoViewerLogo(imgWrap, label.pano_source);
    const attribution = createPanoAttribution(imgWrap, { compact: true });
    creditImage = (source) => {
      if (source === 'crop') {
        logo.showSourceLogo();
        attribution.show(label.pano_data?.attribution);
      } else {
        logo.hide();
        attribution.hide();
      }
    };
    creditImage(card.dataset.imageSource);
    card.appendChild(imgWrap);

    const body = document.createElement('figcaption');
    body.className = 'lvg-card-body';
    const questionRow = document.createElement('div');
    questionRow.className = 'lvg-card-question-row';
    const question = document.createElement('span');
    question.className = 'lvg-card-question';
    // The translations deliberately contain <b> emphasis around the label-type name; they're our own locale files.
    question.innerHTML = i18next.t(`validate:top-ui.title.${typeKebab}`);
    questionRow.appendChild(question);
    this.#attachTypeTooltip(questionRow, question, label, typeKebab);
    questionRow.appendChild(this.#buildShareChip(label));
    body.appendChild(questionRow);

    const actions = document.createElement('div');
    actions.className = 'lvg-card-actions';
    [['Agree', 'lvg-btn-agree'], ['Disagree', 'lvg-btn-disagree'], ['Unsure', 'lvg-btn-unsure']]
      .forEach(([result, cls]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `lvg-btn ${cls}`;
        button.textContent = i18next.t(`common:${result.toLowerCase()}`);
        button.addEventListener('click', () => this.#validate(card, entry, result));
        actions.appendChild(button);
      });
    body.appendChild(actions);
    card.appendChild(body);
    return card;
  }

  /**
   * Builds the card's share chip: the same pill + popover the label-detail popup uses (ShareWidget, #456), pointed
   * at this label's public /label/:id spotlight page — so a visitor who spots something zany or particularly
   * problematic can pass it along, straight from the landing page.
   *
   * @param {Record<string, any>} label - The card's label from /label/labels.
   * @returns {HTMLElement}
   */
  #buildShareChip(label) {
    // .label-detail__share supplies the popover's positioning anchor; .lvg-share pushes the chip to the row's end.
    const { wrap, trigger } = ShareWidget.buildChip('lvg-share');

    // Surface + label attribution for analytics; ShareWidget logs its own generic Share_* events on top.
    trigger.addEventListener('click', () => {
      window.logWebpageActivity(`Click_module=LandingValidationGridShare_labelId=${label.label_id}`);
    });

    const widget = new ShareWidget(trigger, { host: wrap });
    // The title feeds the native sheet and the email subject, so it carries the descriptive text, not "Share".
    const typeName = util.misc.labelTypeName(label.label_type);
    const shareText = i18next.t('common:share.text', { labelType: typeName });
    widget.setTarget({
      url: `${window.location.origin}/label/${label.label_id}`,
      title: shareText,
      text: shareText,
    });
    return wrap;
  }

  /**
   * Turns the question's bolded label-type name into the "what is this?" trigger: dotted-underlined and focusable,
   * revealing the type's one-line explanation (the Explore tutorial's intro copy, already translated in every
   * locale). Piggybacking on the term itself keeps the card free of extra controls.
   *
   * Accessible tooltip pattern: shown on hover and on keyboard focus, click/tap-to-pin for touch users,
   * dismissable with Escape (WCAG 1.4.13). Hover-hide runs on a grace delay because the pointer crosses plain
   * question text between the (possibly wrapped) term and the tooltip spanning the top of the row.
   *
   * @param {HTMLElement} row - The question row (the tooltip's positioning anchor).
   * @param {HTMLElement} question - The question span whose <b> holds the label-type name.
   * @param {Record<string, any>} label - The card's label from /label/labels.
   * @param {string} typeKebab - The label type in kebab-case (e.g. 'curb-ramp'), as used in locale keys.
   */
  #attachTypeTooltip(row, question, label, typeKebab) {
    const term = question.querySelector('b');
    if (!term) return; // A translation without <b> emphasis just goes without the tooltip.

    const tip = document.createElement('span');
    tip.className = 'lvg-type-tip';
    tip.id = `lvg-type-tip-${label.label_id}`;
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    // Our own locale strings; they contain <b>/<br> markup by design.
    tip.innerHTML = i18next.t(`common:mission-start-tutorial.${typeKebab}.slide-1.description`);
    row.appendChild(tip);

    term.className = 'lvg-type-term';
    term.tabIndex = 0;
    term.setAttribute('aria-describedby', tip.id);

    let pinned = false;
    let logged = false;
    let hideTimer;
    const show = () => {
      clearTimeout(hideTimer);
      tip.hidden = false;
      if (!logged) {
        logged = true;
        window.logWebpageActivity(`Click_module=LandingValidationGridInfo_labelType=${label.label_type}`);
      }
    };
    const hide = () => {
      pinned = false;
      tip.hidden = true;
    };
    const scheduleHide = () => {
      if (!pinned && document.activeElement !== term) hideTimer = setTimeout(hide, 250);
    };
    term.addEventListener('mouseenter', show);
    term.addEventListener('mouseleave', scheduleHide);
    tip.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    tip.addEventListener('mouseleave', scheduleHide);
    term.addEventListener('focus', show);
    term.addEventListener('blur', () => {
      if (!pinned) hide();
    });
    term.addEventListener('click', () => {
      if (pinned) {
        hide();
      } else {
        pinned = true;
        show();
      }
    });
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !tip.hidden) hide();
    });
  }

  /**
   * Submits the visitor's validation, shows a brief thanks state, then swaps in a fresh label.
   * @param {HTMLElement} card - The card being validated.
   * @param {LabelEntry} entry - The card's entry.
   * @param {string} result - 'Agree', 'Disagree', or 'Unsure'.
   */
  async #validate(card, entry, result) {
    const label = entry.label;
    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (card.querySelectorAll('.lvg-btn'));
    buttons.forEach((button) => {
      button.disabled = true;
    });
    window.logWebpageActivity(`Click_module=LandingValidationGrid_result=${result}_labelId=${label.label_id}`);

    // Mirror the Gallery's static-image validation payload: canvas_* describe where the label sits within the
    // rendered image. Off the same fraction the marker is drawn from, so the two can't disagree (#2660).
    const img = card.querySelector('.lvg-card-photo');
    const { x: fracX, y: fracY } = LandingValidationGrid.#markerFraction(entry, card.dataset.imageSource);
    const timestamp = new Date();
    const payload = {
      label_id: label.label_id,
      label_type: label.label_type,
      validation_result: result,
      severity: label.severity,
      tags: label.tags,
      canvas_width: Math.round(img.clientWidth),
      canvas_height: Math.round(img.clientHeight),
      canvas_x: Math.round(fracX * img.clientWidth),
      canvas_y: Math.round(fracY * img.clientHeight),
      heading: label.heading,
      pitch: label.pitch,
      zoom: label.zoom,
      start_timestamp: timestamp,
      end_timestamp: timestamp,
      source: 'LandingPage',
      undone: false,
      redone: false,
      viewer_type: card.dataset.imageSource === 'crop' ? 'StaticCrop' : 'StaticApi',
    };

    try {
      // A first-time visitor has no session (#4643), so this vote may be their first-ever write: lazyIdentityFetch
      // mints the anonymous session on an auth-shaped failure and retries once (#4442).
      const response = await util.lazyIdentityFetch('/labelmap/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`validation failed: ${response.status}`);

      // Thanks state (announced by the grid's aria-live region), then swap in the next label.
      const chosen = card.querySelector(`.lvg-btn-${result.toLowerCase()}`);
      chosen?.classList.add('lvg-btn-chosen');
      const thanks = document.createElement('span');
      thanks.className = 'lvg-card-thanks';
      thanks.textContent = i18next.t('common:map.thanks');
      card.querySelector('.lvg-card-question-row').replaceWith(thanks);
      setTimeout(() => this.#replaceCard(card), LandingValidationGrid.#THANKS_MS);
    } catch (e) {
      console.error('Failed to submit validation', e);
      buttons.forEach((button) => {
        button.disabled = false;
      });
    }
  }

  /**
   * Swaps a finished (or broken) card for the next pooled label, refilling the pool in the background as it runs
   * low. With the pool empty the card is simply removed, and an emptied-out grid hides the whole section.
   * @param {HTMLElement} card - The card to replace.
   */
  async #replaceCard(card) {
    if (this.#pool.length <= LandingValidationGrid.#REFILL_THRESHOLD) {
      const refill = this.#loadBatch(LandingValidationGrid.#REFILL_FETCH);
      if (this.#pool.length === 0) await refill;
    }
    const hadFocus = card.contains(document.activeElement);
    const index = [...this.#grid.children].indexOf(card);
    const entry = this.#pool.shift();
    if (entry) {
      const fresh = this.#buildCard(entry, index);
      card.replaceWith(fresh);
      if (hadFocus) fresh.querySelector('.lvg-btn')?.focus();
    } else {
      card.remove();
      if (this.#grid.querySelectorAll('.lvg-card').length === 0) this.#section.hidden = true;
    }
  }
}
